import type { CSSProperties } from 'react';
import type { ThemeContextValue } from '@course-authoring/shared-types';
import { createContentBlockStyles } from '@course-authoring/ui-blocks';

type ContentBlockStyles = ReturnType<typeof createContentBlockStyles>;
type EditorTokens = ThemeContextValue['tokens'];

export interface ClickExpandEditorStyles {
  container: CSSProperties;
  header: CSSProperties;
  titleLevelControls: CSSProperties;
  titleLevelLabel: CSSProperties;
  titleLevelButtonGroup: CSSProperties;
  titleLevelButton: CSSProperties;
  titleLevelButtonActive: CSSProperties;
  titleInput: CSSProperties;
  descriptionEditor: CSSProperties;
  expandTypeSection: CSSProperties;
  expandTypeLabel: CSSProperties;
  expandTypeButton: CSSProperties;
  expandTypeButtonActive: CSSProperties;
  expandTypeButtonInactive: CSSProperties;
  imageSizeSection: CSSProperties;
  topicsList: CSSProperties;
  topicContainer: CSSProperties;
  topicHeader: CSSProperties;
  topicLabel: CSSProperties;
  deleteButton: CSSProperties;
  fieldLabel: CSSProperties;
  fieldWrapper: CSSProperties;
  topicTitleInput: CSSProperties;
  contentEditor: CSSProperties;
  imagePlaceholder: CSSProperties;
  imagePreviewContainer: CSSProperties;
  imagePreviewFrame: CSSProperties;
  imagePreviewImage: CSSProperties;
  imageActions: CSSProperties;
  imageActionButton: CSSProperties;
  addTopicButton: CSSProperties;
  addTopicButtonDisabled: CSSProperties;
}

export const clickExpandEditorInnerStyles = `
  .click-expand-editor p {
    margin: 0;
  }
  .click-expand-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

export const clickExpandToolbarStyles: Record<string, CSSProperties> = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #9ca3af',
  },
  toolbarButton: {
    minWidth: '32px',
    height: '32px',
    padding: '0 8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolbarButtonActive: {
    backgroundColor: '#3b82f6',
    color: '#fff',
  },
};

export function createClickExpandEditorStyles(
  tokens: EditorTokens,
  baseStyles: ContentBlockStyles,
  isFocused: boolean,
  isMobile: boolean
): ClickExpandEditorStyles {
  const clickExpandWidth = isMobile
    ? '90%'
    : '80%';

  return {
    container: {
      ...baseStyles.clickExpandContainer,
      width: clickExpandWidth,
      maxWidth: '100%',
      margin: '0 auto',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: isFocused ? tokens.brand.primary : 'transparent',
      transition: 'border-color 150ms',
      boxSizing: 'border-box',
    },
    header: {
      ...baseStyles.clickExpandHeader,
    },
    titleLevelControls: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.xs,
    },
    titleLevelLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    titleLevelButtonGroup: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
    },
    titleLevelButton: {
      minWidth: '40px',
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 150ms, color 150ms, border-color 150ms',
    },
    titleLevelButtonActive: {
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
      borderColor: tokens.brand.primary,
    },
    titleInput: {
      width: '100%',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.h2.size,
      fontWeight: tokens.typography.h2.weight,
      lineHeight: tokens.typography.h2.lineHeight,
      color: tokens.text.primary,
      padding: 0,
      marginBottom: tokens.spacing.sm,
    },
    descriptionEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.muted,
      lineHeight: tokens.typography.body.lineHeight,
    },
    expandTypeSection: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.lg,
    },
    expandTypeLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    expandTypeButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      cursor: 'pointer',
      transition: 'all 150ms',
      fontFamily: tokens.typography.fontFamily,
    },
    expandTypeButtonActive: {
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
      borderColor: tokens.brand.primary,
    },
    expandTypeButtonInactive: {
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.muted,
      borderColor: tokens.borders.default,
    },
    imageSizeSection: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.lg,
    },
    topicsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    topicContainer: {
      position: 'relative',
      backgroundColor: tokens.backgrounds.app,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.lg,
      border: `1px solid ${tokens.borders.default}`,
      boxSizing: 'border-box',
    },
    topicHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: tokens.spacing.md,
    },
    topicLabel: {
      fontSize: '13px',
      fontWeight: 700,
      color: tokens.text.primary,
    },
    deleteButton: {
      width: '24px',
      height: '24px',
      padding: 0,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: tokens.radius.sm,
      color: tokens.text.muted,
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 150ms, background-color 150ms',
    },
    fieldLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      marginBottom: tokens.spacing.xs,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    fieldWrapper: {
      marginBottom: tokens.spacing.md,
    },
    topicTitleInput: {
      width: '100%',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
    },
    contentEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      lineHeight: tokens.typography.body.lineHeight,
    },
    imagePlaceholder: {
      width: '100%',
      padding: tokens.spacing.md,
      backgroundColor: 'transparent',
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      color: tokens.text.muted,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'border-color 150ms, color 150ms',
      textAlign: 'center',
      boxSizing: 'border-box',
    },
    imagePreviewContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
    },
    imagePreviewFrame: {
      width: '80px',
      height: '60px',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      flexShrink: 0,
      backgroundColor: 'transparent',
    },
    imagePreviewImage: {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: tokens.radius.sm,
    },
    imageActions: {
      display: 'flex',
      gap: tokens.spacing.sm,
    },
    imageActionButton: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.muted,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
    },
    addTopicButton: {
      width: '100%',
      padding: tokens.spacing.md,
      backgroundColor: 'transparent',
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      color: tokens.text.muted,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'border-color 150ms, color 150ms',
      textAlign: 'center',
      boxSizing: 'border-box',
    },
    addTopicButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
}
